import { Search, X } from "lucide-react";
import * as React from "react";

import { Input } from "@/components/ui/input";
import { useT } from "@/lib/i18n/use-t";
import { cn } from "@/lib/utils";
import { T } from "./search-input.i18n";

type SearchInputProps = Omit<React.ComponentProps<"input">, "type" | "value" | "onChange"> & {
  value: string;
  onValueChange: (value: string) => void;
  /** Classes for the input itself; `className` sizes the wrapper (`w-64`, `flex-1 max-w-sm`). */
  inputClassName?: string;
};

/**
 * Free-text search field (DESIGN.md › Components › Search inputs): a search
 * glyph on the left and, once there is a query, a clear × on the right that
 * empties it and hands focus back so the next search can be typed straight away.
 *
 * `type="search"` keeps the searchbox role, but the browser's own cancel button
 * is hidden: Chromium draws an unstyled blue × and Firefox draws none, so ours
 * is the only one, in every browser.
 */
const SearchInput = React.forwardRef<HTMLInputElement, SearchInputProps>(
  ({ value, onValueChange, className, inputClassName, placeholder, disabled, ...props }, ref) => {
    const t = useT(T);
    const innerRef = React.useRef<HTMLInputElement>(null);
    React.useImperativeHandle(ref, () => innerRef.current as HTMLInputElement);

    function clear() {
      onValueChange("");
      innerRef.current?.focus();
    }

    return (
      <div className={cn("relative", className)}>
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          ref={innerRef}
          type="search"
          value={value}
          onChange={(e) => onValueChange(e.target.value)}
          placeholder={placeholder}
          // A placeholder is not a label, but a search field with nothing else
          // naming it is better off announcing the placeholder than nothing.
          aria-label={
            props["aria-label"] ?? (props.id || props["aria-labelledby"] ? undefined : placeholder)
          }
          disabled={disabled}
          className={cn(
            "pl-9 [&::-webkit-search-cancel-button]:hidden [&::-webkit-search-decoration]:hidden",
            value && "pr-9",
            inputClassName,
          )}
          {...props}
        />
        {value && !disabled && (
          <button
            type="button"
            // Clearing a query is navigation, not an edit: a dialog's discard
            // guard counts button clicks as edits unless told otherwise.
            data-dialog-guard-ignore
            onClick={clear}
            aria-label={t.clearSearch}
            className="absolute right-3 top-1/2 -translate-y-1/2 rounded-sm text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
    );
  },
);
SearchInput.displayName = "SearchInput";

export { SearchInput };
